/**
 * Invite d'installation de Geo Nerd sur l'écran d'accueil (45 du TODO).
 *
 * Chrome et Edge (Android, ordinateur) émettent `beforeinstallprompt` : on le
 * garde pour le déclencher depuis notre propre bouton, au bon moment, plutôt
 * que la mini-barre du navigateur au premier chargement. Safari sur iPhone n'a
 * pas d'API : on y affiche le mode d'emploi (Partager, puis « Sur l'écran
 * d'accueil »), qui est aussi le seul moyen d'y recevoir des notifications.
 *
 * Proposée seulement à un joueur qui revient (deux défis du jour ou cinq
 * parties terminées), jamais dans l'application déjà installée, et plus pendant
 * 30 jours après un « Plus tard ».
 */
const DISMISSED_KEY = "geonerd.installDismissedAt";
const SNOOZE_MS = 30 * 86400000;

let deferredPrompt = null;

/** À appeler au démarrage : l'événement part tôt, avant que la page ne s'en serve. */
export function captureInstallPrompt() {
    if (typeof window === "undefined") return;
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        document.dispatchEvent(new CustomEvent("geonerd:installable"));
    });
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
    });
}

export function isStandalone() {
    return Boolean(window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone);
}

export function isIos() {
    const ua = navigator.userAgent || "";
    return /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function canPrompt() {
    return Boolean(deferredPrompt);
}

/** Un joueur qui revient : deux défis du jour joués, ou cinq parties terminées. */
export function isEngaged() {
    let dailies = 0;
    let games = 0;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (/^geonerd\.daily(\.hard)?\.\d{4}-\d{2}-\d{2}$/.test(key)) dailies++;
        else if (key.endsWith(".gamesPlayed")) games += parseInt(localStorage.getItem(key), 10) || 0;
    }
    return dailies >= 2 || games >= 5;
}

export function isSnoozed(now = Date.now()) {
    const at = parseInt(localStorage.getItem(DISMISSED_KEY), 10);
    return Number.isFinite(at) && now - at < SNOOZE_MS;
}

export function shouldOffer() {
    if (isStandalone() || isSnoozed() || !isEngaged()) return false;
    return canPrompt() || isIos();
}

export function snooze() {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
}

/** @returns {Promise<boolean>} vrai si le joueur a accepté l'installation. */
export async function promptInstall() {
    if (!deferredPrompt) return false;
    const prompt = deferredPrompt;
    deferredPrompt = null;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    return outcome === "accepted";
}

/**
 * Carte d'installation, insérée avant `anchor`. Le bouton « Installer » n'existe
 * que là où le navigateur sait installer ; sur iPhone, le mode d'emploi le remplace.
 */
export function renderInstallCard(anchor) {
    if (!anchor || document.querySelector(".install-card") || !shouldOffer()) return;
    const t = (k) => window.geoNerdApp.languageManager.t(k);
    const ios = !canPrompt() && isIos();
    anchor.insertAdjacentHTML("beforebegin", `
        <div class="install-card" role="note">
            <img class="install-card-icon" src="/favicon/android-chrome-192x192.png" alt="" width="48" height="48">
            <div class="install-card-text">
                <p class="install-card-title">${t("install.title")}</p>
                <p class="install-card-body">${t(ios ? "install.iosBody" : "install.body")}</p>
            </div>
            <div class="install-card-actions">
                ${ios ? "" : `<button type="button" class="btn install-card-cta">${t("install.cta")}</button>`}
                <button type="button" class="install-card-later">${t("install.later")}</button>
            </div>
        </div>`);
    const card = document.querySelector(".install-card");
    card.querySelector(".install-card-cta")?.addEventListener("click", async () => {
        await promptInstall();
        card.remove();
    });
    card.querySelector(".install-card-later").addEventListener("click", () => {
        snooze();
        card.remove();
    });
}
